/*
 * Plan tab to-do list - pure helpers.
 *
 * A project's `todos` field is an array of {id, text, done, createdAt, doneAt}.
 * These helpers are pure so they can be unit tested in Node (test/plan-todos.test.js)
 * while app.jsx imports them for the browser bundle. Every helper returns a NEW
 * array; nothing mutates the project object in place, because the caller feeds
 * the result straight into onPatch(slug, {todos}) → POST /api/projects.
 */

export function todoId() {
  return "td_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

/** Tolerate a missing / malformed field: always hand back a clean array. */
export function normalizeTodos(todos) {
  if (!Array.isArray(todos)) return [];
  return todos
    .filter((x) => x && typeof x === "object")
    .map((x) => ({
      id: String(x.id || todoId()),
      text: String(x.text == null ? "" : x.text),
      done: Boolean(x.done),
      createdAt: x.createdAt || null,
      doneAt: x.doneAt || null,
    }));
}

/** Append a trimmed item. Empty / whitespace-only text is a no-op. */
export function addTodo(todos, text, now) {
  const body = String(text == null ? "" : text).trim();
  const list = normalizeTodos(todos);
  if (!body) return list;
  return list.concat([{ id: todoId(), text: body, done: false, createdAt: now || Date.now(), doneAt: null }]);
}

/** Flip one item's done flag, stamping/clearing doneAt. */
export function toggleTodo(todos, id, now) {
  return normalizeTodos(todos).map((x) =>
    x.id === id ? { ...x, done: !x.done, doneAt: !x.done ? (now || Date.now()) : null } : x);
}

export function removeTodo(todos, id) {
  return normalizeTodos(todos).filter((x) => x.id !== id);
}

/** Open items first, done items after; original order kept inside each group. */
export function sortTodos(todos) {
  const list = normalizeTodos(todos);
  return list.filter((x) => !x.done).concat(list.filter((x) => x.done));
}

export function todoCounts(todos) {
  const list = normalizeTodos(todos);
  return { done: list.filter((x) => x.done).length, total: list.length };
}

/** `- [ ]` / `- [x]` lines for PROJECT_STATE.md. */
export function todosMarkdown(todos) {
  const list = sortTodos(todos);
  if (!list.length) return ["_none_"];
  return list.map((x) => `- [${x.done ? "x" : " "}] ${x.text}`);
}
